import { persisted } from 'svelte-local-storage-store';
import type { SearchHistoryItem, ServiceType } from '../../../../core/types';
import { MAX_HISTORY_ITEMS, SEARCH_HISTORY_LOCAL_STORAGE_KEY } from '../../constants';
import { get } from 'svelte/store';

const createSearchHistoryStore = () => {
    const store = persisted<SearchHistoryItem[]>(SEARCH_HISTORY_LOCAL_STORAGE_KEY, []);

    // 최신 항목 정렬, 중복 제거 및 제한 함수
    const sortAndLimitItems = (items: SearchHistoryItem[]): SearchHistoryItem[] => {
        const sorted = items
            .slice()
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

        const uniqueItems: SearchHistoryItem[] = [];
        const seen = new Set<string>();

        for (const item of sorted) {
            const query = item.query.trim();
            const country = item.filters.country || '';
            const serviceTypes = [...(item.filters.serviceTypes || [])].sort().join(',');
            const key = `${query}|${country}|${serviceTypes}`;

            if (!seen.has(key)) {
                seen.add(key);
                uniqueItems.push(item);
            }
        }

        return uniqueItems.slice(0, MAX_HISTORY_ITEMS);
    };

    return {
        subscribe: store.subscribe,
        set: store.set,
        update: store.update,
        getState: () => get(store),
        addOrUpdate: (query: string, filters: { country: string; serviceTypes: ServiceType[] }) => {
            const trimmedQuery = query.trim();
            if (!trimmedQuery) return;

            const now = new Date().toISOString();
            const current = get(store);

            // 쿼리와 필터 모두 비교하여 동일한 항목 찾기
            const idx = current.findIndex(h => {
                if (h.query !== trimmedQuery) return false;

                // Country comparison (normalize null/undefined/empty)
                const hCountry = h.filters.country || undefined;
                const newCountry = filters.country || undefined;
                if (hCountry !== newCountry) return false;

                // ServiceTypes comparison
                const hTypes = new Set(h.filters.serviceTypes);
                const newTypes = new Set(filters.serviceTypes);

                if (hTypes.size !== newTypes.size) return false;
                for (const type of newTypes) {
                    if (!hTypes.has(type)) return false;
                }

                return true;
            });

            if (idx >= 0) {
                // 기존 항목 업데이트
                const updated = [...current];
                updated[idx] = { ...updated[idx], updatedAt: now };
                store.set(sortAndLimitItems(updated));
            } else {
                // 새 항목 추가
                const newItem = {
                    query: trimmedQuery,
                    filters,
                    updatedAt: now,
                };
                store.set(sortAndLimitItems([...current, newItem]));
            }
        },
        remove: (index: number) => {
            const current = get(store);

            // 유효한 인덱스인지 확인
            if (index < 0 || index >= current.length) return;

            const filtered = current.filter((_, i) => i !== index);
            store.set(filtered);
        },
    };
};

export const searchHistoryStore = createSearchHistoryStore();
